import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, Headphones, Save, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { questionService, type Question } from "@/services/questionService";
import { getAutomaticDifficulty, LISTENING_AUDIO_TYPE_OPTIONS } from "@/data/tefStructure";
import { mediaService } from "@/services/mediaService";
import { storageService } from "@/services/storageService";
import { isSupabaseConfigured } from "@/lib/supabaseClient";
import { getCompetencyLabel } from "@/data/tefScoreScale";
import { useAuth } from "@/contexts/AuthContext";
import { validateQuestion, normalizeQuestion } from "@/utils/questionValidation";
import { normalizeQuestionOptions } from "@/utils/questionNormalizer";
import { AudioPlayerWithSpeed } from "@/components/audio/AudioPlayerWithSpeed";

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
    reader.readAsDataURL(file);
  });
};

export default function NewQuestion() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const isEdit = !!id;
  const [originalQuestion, setOriginalQuestion] = useState<Question | null>(null);

  const [competence, setCompetence] = useState<"reading" | "listening" | "writing">("reading");
  const [section, setSection] = useState("Section A");
  const [subSection, setSubSection] = useState("");
  const isListeningSectionA = competence === "listening" && section === "Dialogues associés à des images";
  const [type, setType] = useState<"mcq" | "written">("mcq");
  const [title, setTitle] = useState("");
  const [instruction, setInstruction] = useState("");
  const [question, setQuestion] = useState("");
  const [text, setText] = useState("");
  const [audioType, setAudioType] = useState("");
  const [tags, setTags] = useState("");
  const [audioUrl, setAudioUrl] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<File | null>(null);

  // States for existing media loading and tracking removal
  const [existingAudioUrl, setExistingAudioUrl] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [audioRemoved, setAudioRemoved] = useState(false);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);

  // Computed automatic difficulty (no manual selection)
  const autoDifficulty = useMemo(() => getAutomaticDifficulty({
    competency: competence,
    section,
    audioType
  }), [competence, section, audioType]);
  
  // MCQ alternatives
  const [alternatives, setAlternatives] = useState({
    A: "",
    B: "",
    C: "",
    D: ""
  });

  // Listening Section A image alternatives
  const [optionImages, setOptionImages] = useState<{
    A: string | File | null;
    B: string | File | null;
    C: string | File | null;
    D: string | File | null;
  }>({
    A: null,
    B: null,
    C: null,
    D: null
  });

  const [correctAnswer, setCorrectAnswer] = useState("A");
  const [explanation, setExplanation] = useState("");

  // Auto-set section default when competence changes in creation mode
  useEffect(() => {
    if (!isEdit) {
      if (competence === "reading") {
        setSection("Documents divers");
      } else if (competence === "listening") {
        setSection("Dialogues associés à des images");
      } else {
        setSection("Fait Divers");
        setType("written"); // Force written type for Writing competence
      }
    }
  }, [competence, isEdit]);

  // Handle forcing MCQ or Written type based on competence
  useEffect(() => {
    if (competence === "listening") {
      setType("mcq");
    } else if (competence === "writing") {
      setType("written");
    } else if (competence === "reading") {
      setType("mcq");
    }
  }, [competence]);

  // Load existing question details in Edit Mode
  useEffect(() => {
    if (isEdit && id) {
      const loadEditQuestion = async () => {
        const q = await questionService.getQuestionById(id);
        if (q) {
          setOriginalQuestion(q);
          setTitle(q.title);
        
        // Normalize competence (case insensitive + schema differences)
        const comp = (q.competence || (q as any).competency || "reading").toLowerCase() as "reading" | "listening" | "writing";
        setCompetence(comp);
        
        // Normalize section mapping for UI selectors compatibility
        let sect = q.section || "Section A";
        if (comp === "listening") {
          const listeningOptions = [
            "Dialogues associés à des images",
            "Annonces publiques",
            "Messages courts / situations téléphoniques",
            "Micro-trottoirs",
            "Chroniques radio",
            "Interviews",
            "Reportages",
            "Documents divers"
          ];
          if (listeningOptions.includes(sect)) {
            setSection(sect);
          } else {
            const sKey = (q as any).sectionKey || "section_b";
            if (sKey === "section_a") {
              setSection("Dialogues associés à des images");
            } else if (sKey === "section_c") {
              setSection("Interviews");
            } else if (sKey === "section_d") {
              setSection("Documents divers");
            } else {
              setSection("Annonces publiques");
            }
          }
        } else if (comp === "reading") {
          const readingOptions = [
            "Documents divers",
            "Phrases lacunaires / Textes lacunaires",
            "Lecture rapide de textes et de graphiques",
            "Documents administratifs et professionnels",
            "Articles de presse"
          ];
          if (readingOptions.includes(sect)) {
            setSection(sect);
          } else {
            const sKey = (q as any).sectionKey || "section_a";
            const sectLower = sect.trim().toLowerCase();
            if (sKey === "section_a" || sectLower.includes("anúncio") || sectLower.includes("annonce") || sectLower.includes("classificado")) {
              setSection("Documents divers");
            } else if (sKey === "section_b" || sectLower.includes("notícia") || sectLower.includes("article") || sectLower.includes("presse")) {
              setSection("Articles de presse");
            } else if (sKey === "section_c" || sectLower.includes("opinion") || sectLower.includes("graphique") || sectLower.includes("lecture rapide")) {
              setSection("Lecture rapide de textes et de graphiques");
            } else if (sKey === "section_d" || sectLower.includes("lacunaire") || sectLower.includes("grammaire") || sectLower.includes("phrase")) {
              setSection("Phrases lacunaires / Textes lacunaires");
            } else {
              setSection("Documents divers");
            }
          }
        } else {
          const sectLower = sect.trim().toLowerCase();
          if (sectLower.includes("fait divers") || sectLower.includes("article / fait divers") || sectLower.includes("tâche 1") || sectLower.includes("tache 1")) {
            sect = "Fait Divers";
          } else if (sectLower.includes("opinion") || sectLower.includes("tâche 2") || sectLower.includes("tache 2")) {
            sect = "Letra de Opinião";
          }
          setSection(sect);
        }
        
        // Normalize type
        const normalizedType = (q as any).type === "multiple_choice" ? "mcq" : (q.type || "mcq");
        setType(normalizedType);
        
        setInstruction(q.instruction || "");
        setQuestion(q.question || (q as any).prompt || "");
        
        // Resolve text / bodyText
        setText(q.text || (q as any).bodyText || "");
        setTags(q.tags ? q.tags.join(", ") : "");
        
        // Restore audioType if saved
        setAudioType((q as any).audioType || "");
        if (q.alternatives) {
          setAlternatives(q.alternatives);
        }
        
        // Restore subSection if saved
        let loadedSub = q.subSection || (q as any).sub_section || q.metadata?.subSection || q.metadata?.sub_section || "";
        if (!loadedSub && (q.competence === "Reading" || (q as any).competency === "reading")) {
          if (q.section === "Lecture rapide de textes et de graphiques") {
            if (q.imageMediaId || q.imageUrl || (q as any).supportImage) {
              loadedSub = "lecture_rapide_graphiques";
            } else {
              loadedSub = "lecture_rapide_textes";
            }
          } else if (q.section === "Phrases lacunaires / Textes lacunaires" || q.section === "Phrases et textes lacunaires") {
            const bodyStr = String(q.text || (q as any).bodyText || "").trim();
            const titStr = String(q.title || "").trim().toLowerCase();
            const dotCount = (bodyStr.match(/\.\.\.|\([_.]{3,}\)|_{3,}/g) || []).length;
            if (bodyStr.length < 150 && (dotCount <= 1 || titStr.includes("phrase") || titStr.includes("q1") || titStr.includes("q2") || titStr.includes("q3") || titStr.includes("q4"))) {
              loadedSub = "phrases_lacunaires";
            } else {
              loadedSub = "textes_lacunaires";
            }
          }
        }
        setSubSection(loadedSub);
        
        // Reset removed states
        setAudioRemoved(false);
        setImageRemoved(false);
        setAudioUrl(null);
        setImageUrl(null);

        // Resolve option images, audio and illustrative images asynchronously in one cohesive sequence
        const loadAllMedia = async () => {
          try {
            // Load supportive audio if exists
            if (q.audioMediaId) {
              const url = await mediaService.getMediaUrl(q.audioMediaId);
              if (url) setExistingAudioUrl(url);
            } else if (q.audioUrl) {
              setExistingAudioUrl(q.audioUrl);
            } else {
              setExistingAudioUrl(null);
            }

            // Load supportive illustrative image if exists
            if (q.imageMediaId) {
              const url = await mediaService.getMediaUrl(q.imageMediaId);
              if (url) setExistingImageUrl(url);
            } else if (q.imageUrl) {
              setExistingImageUrl(q.imageUrl);
            } else {
              setExistingImageUrl(null);
            }
            
            // Resolve option images asynchronously (Section A Listening)
            if (q.options) {
              const loadedImages: any = { A: null, B: null, C: null, D: null };
              for (const opt of q.options || []) {
                if (opt.mediaId) {
                  const url = await mediaService.getMediaUrl(opt.mediaId);
                  if (url) {
                    loadedImages[opt.label] = url;
                  }
                } else if (opt.imageUrl) {
                  loadedImages[opt.label] = opt.imageUrl;
                }
              }
              setOptionImages(loadedImages);
            }
          } catch (err) {
            console.error("Erro ao carregar mídias de edição:", err);
          } finally {
            setLoading(false);
          }
        };

        loadAllMedia();
        
        setCorrectAnswer(q.correctAnswer || "A");
        setExplanation(q.explanation || "");
      } else {
        setLoading(false);
      }
    };
    
    loadEditQuestion();
  } else {
    setLoading(false);
  }
}, [id, isEdit]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("[NewQuestion] handleSave called");

    if (!isAdmin) {
      alert(t("newQuestion.noPermission"));
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      const formattedCompetence = (competence.charAt(0).toUpperCase() + competence.slice(1)) as "Reading" | "Listening" | "Writing";
      const formattedDifficulty = autoDifficulty.difficultyLegacy;

      // Centralized Validation
      const tempQ = {
        title: (competence === "listening" ? "Listening Temp" : title) || (isEdit ? originalQuestion?.title : ""),
        competence: formattedCompetence,
        section,
        instruction,
        question,
        text,
        options: isListeningSectionA ? optionImages : alternatives,
        correctAnswer,
        explanation,
        subSection,
        type: competence === "writing" ? "written" : "mcq"
      };

      const validation = validateQuestion(tempQ);

      if (!validation.isValid) {
        alert(t("newQuestion.validationError", { errors: validation.errors.join("\n") }));
        setIsSaving(false);
        return;
      }

      // 1. Generate stable finalId upfront to group assets in folder
      const finalId = id || `q_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

      // 2. Title Autogeneration for Listening
      let finalTitle = title;
      if (competence === "listening") {
        if (isEdit && originalQuestion?.title) {
          finalTitle = originalQuestion.title;
        } else {
          const questions = questionService.getQuestionsSync();
          const listeningQuestions = questions.filter(
            q => q.competence.toLowerCase() === "listening" || (q as any).competency?.toLowerCase() === "listening"
          );
          finalTitle = `Conversation ${listeningQuestions.length + 1}`;
        }
      } else {
        finalTitle = title || (isEdit ? t("newQuestion.editQuestion") : t("newQuestion.newQuestion"));
      }

      // 3. Save media (Supabase Storage if configured, or fallback to IndexedDB)
      let mediaImage = originalQuestion?.media?.image || null;
      let mediaAudio = originalQuestion?.media?.audio || null;
      let mediaOptionImages = originalQuestion?.media?.optionImages || {};

      let savedImageMediaId = originalQuestion?.imageMediaId || "";
      let savedAudioMediaId = originalQuestion?.audioMediaId || "";
      let processedOptions: any[] | undefined = undefined;

      if (isSupabaseConfigured) {
        // Image Processing
        if (imageRemoved) {
          if (mediaImage?.path) {
            await storageService.deleteQuestionMedia(mediaImage.path, "question-images");
          }
          mediaImage = null;
        }

        if (imageUrl) {
          if (mediaImage?.path) {
            await storageService.deleteQuestionMedia(mediaImage.path, "question-images");
          }
          const uploaded = await storageService.uploadQuestionImage(imageUrl, finalId);
          mediaImage = uploaded;
        }

        // Audio Processing
        if (audioRemoved) {
          if (mediaAudio?.path) {
            await storageService.deleteQuestionMedia(mediaAudio.path, "question-audios");
          }
          mediaAudio = null;
        }

        if (audioUrl) {
          if (mediaAudio?.path) {
            await storageService.deleteQuestionMedia(mediaAudio.path, "question-audios");
          }
          const uploaded = await storageService.uploadQuestionAudio(audioUrl, finalId);
          mediaAudio = uploaded;
        }

        // Option Images Processing (Listening Section A)
        if (isListeningSectionA) {
          const updatedOptionImages: any = { ...mediaOptionImages };
          processedOptions = await Promise.all(
            (["A", "B", "C", "D"] as const).map(async (label) => {
              const fileOrUrl = optionImages[label];

              if (fileOrUrl instanceof File) {
                const oldPath = mediaOptionImages[label]?.path;
                if (oldPath) {
                  await storageService.deleteQuestionMedia(oldPath, "question-images");
                }
                const uploaded = await storageService.uploadQuestionImage(fileOrUrl, finalId);
                updatedOptionImages[label] = uploaded;
                return {
                  label,
                  text: "",
                  imageUrl: uploaded.url
                };
              } else if (typeof fileOrUrl === "string") {
                return {
                  label,
                  text: "",
                  imageUrl: fileOrUrl
                };
              } else {
                const oldPath = mediaOptionImages[label]?.path;
                if (oldPath) {
                  await storageService.deleteQuestionMedia(oldPath, "question-images");
                }
                delete updatedOptionImages[label];
                return {
                  label,
                  text: "",
                  imageUrl: ""
                };
              }
            })
          );
          mediaOptionImages = updatedOptionImages;
        }
      } else {
        // Fallback Local Storage Mode (IndexedDB)
        if (imageUrl) {
          try {
            savedImageMediaId = await mediaService.saveMedia(imageUrl);
          } catch (err: any) {
            console.error("Erro ao salvar imagem de suporte:", err);
            alert(err.message || t("newQuestion.imageSaveError"));
            setIsSaving(false);
            return;
          }
        } else if (isEdit && imageRemoved) {
          savedImageMediaId = "";
        }

        if (audioUrl) {
          try {
            savedAudioMediaId = await mediaService.saveMedia(audioUrl);
          } catch (err: any) {
            console.error("Erro ao salvar áudio:", err);
            alert(err.message || t("newQuestion.audioSaveError"));
            setIsSaving(false);
            return;
          }
        } else if (isEdit && audioRemoved) {
          savedAudioMediaId = "";
        }

        if (isListeningSectionA) {
          try {
            processedOptions = await Promise.all(
              (["A", "B", "C", "D"] as const).map(async (label) => {
                const fileOrUrl = optionImages[label];
                let optMediaId = "";
                let optImageUrl = "";

                if (fileOrUrl instanceof File) {
                  optMediaId = await mediaService.saveMedia(fileOrUrl);
                } else if (typeof fileOrUrl === "string") {
                  const safeOptions = Array.isArray(originalQuestion?.options) ? originalQuestion?.options : normalizeQuestionOptions(originalQuestion);
                  const originalOpt = Array.isArray(safeOptions) ? safeOptions.find((o: any) => o.label === label) : null;
                  if (originalOpt?.mediaId) {
                    optMediaId = originalOpt.mediaId;
                  } else {
                    optImageUrl = fileOrUrl;
                  }
                }

                return {
                  label,
                  text: "",
                  imageUrl: optImageUrl,
                  mediaId: optMediaId || undefined
                };
              })
            );
          } catch (err: any) {
            console.error("Erro ao processar mídias das alternativas:", err);
            alert(err.message || t("newQuestion.optionImagesError"));
            setIsSaving(false);
            return;
          }
        }
      }

      const hasImage = isListeningSectionA 
        ? (processedOptions && processedOptions.some(o => o.mediaId || o.imageUrl)) 
        : (isSupabaseConfigured ? !!mediaImage : (!!savedImageMediaId || (isEdit && !imageRemoved && !!originalQuestion?.hasImage)));
      const hasAudio = isSupabaseConfigured ? !!mediaAudio : (!!savedAudioMediaId || (isEdit && !audioRemoved && !!originalQuestion?.hasAudio));

      let normalizedSectionKey = undefined;
      if (competence === "listening") {
        if (section === "Dialogues associés à des images") {
          normalizedSectionKey = "section_a";
        } else if (
          section === "Annonces publiques" ||
          section === "Messages courts / situations téléphoniques" ||
          section === "Micro-trottoirs" ||
          section === "Chroniques radio"
        ) {
          normalizedSectionKey = "section_b";
        } else if (
          section === "Interviews" ||
          section === "Reportages"
        ) {
          normalizedSectionKey = "section_c";
        } else if (section === "Documents divers") {
          normalizedSectionKey = "section_d";
        }
      } else if (competence === "reading") {
        if (section === "Documents divers") {
          normalizedSectionKey = "section_a";
        } else if (section === "Articles de presse") {
          normalizedSectionKey = "section_b";
        } else if (
          section === "Lecture rapide de textes et de graphiques" ||
          section === "Documents administratifs et professionnels"
        ) {
          normalizedSectionKey = "section_c";
        } else if (section === "Phrases lacunaires / Textes lacunaires") {
          normalizedSectionKey = "section_d";
        }
      } else if (competence === "writing") {
        if (section === "Fait Divers") {
          normalizedSectionKey = "section_a";
        } else if (section === "Letra de Opinião") {
          normalizedSectionKey = "section_b";
        }
      }

      // 5. Construct Payload (dual schema compatibility)
      const questionPayload: any = {
        id: finalId,
        title: finalTitle,
        competence: formattedCompetence,
        competency: competence, // lowercase schema
        section: section,
        sectionKey: normalizedSectionKey,
        type: competence === "writing" ? "open_text" : "multiple_choice",
        // Automatic difficulty fields
        difficulty: formattedDifficulty,
        difficultyLabel: autoDifficulty.difficultyLabel,
        probableLevel: autoDifficulty.probableLevel,
        audioType: competence === "listening" ? (audioType || null) : undefined,
        tags: tags ? tags.split(",").map(t => t.trim()).filter(Boolean) : [],
        instruction,
        question,
        text: (isListeningSectionA || competence === "writing") ? "" : text,
        bodyText: (isListeningSectionA || competence === "writing") ? "" : text,
        hasImage: !!hasImage,
        imageUrl: isSupabaseConfigured ? (mediaImage?.url || "") : "",
        hasAudio,
        audioUrl: isSupabaseConfigured ? (mediaAudio?.url || "") : "",
        audioMediaId: savedAudioMediaId || undefined,
        imageMediaId: savedImageMediaId || undefined,
        alternatives: (type === "mcq" && !isListeningSectionA) ? alternatives : undefined,
        options: processedOptions,
        correctAnswer: correctAnswer,
        explanation: explanation,
        createdAt: originalQuestion?.createdAt || new Date().toISOString().split("T")[0],
        updatedAt: new Date().toISOString().split("T")[0],
        isActive: true,
        subSection: subSection || undefined,
        metadata: {
          ...originalQuestion?.metadata,
          subSection: subSection || undefined
        },
        media: isSupabaseConfigured ? {
          image: mediaImage,
          audio: mediaAudio,
          optionImages: isListeningSectionA ? mediaOptionImages : undefined
        } : undefined
      };

      console.log("payload question:", questionPayload);

      // 6. Save using the questionService
      let savedQuestion;
      if (isEdit && id) {
        savedQuestion = await questionService.updateQuestion(id, questionPayload);
        console.log("saved question:", savedQuestion);
        navigate("/questions", { state: { toastMessage: t("newQuestion.successUpdate") } });
      } else {
        savedQuestion = await questionService.createQuestion(questionPayload);
        console.log("saved question:", savedQuestion);
        navigate("/questions", { state: { toastMessage: t("newQuestion.successCreate") } });
      }
    } catch (error: any) {
      console.error("[NewQuestion] save error:", error);
      if (error.code === "DUPLICATE_QUESTION" || error.message?.includes("já estar cadastrada") || error.message?.includes("já existe")) {
        alert(t("newQuestion.duplicateAlert"));
      } else {
        alert(t("newQuestion.saveError", { message: error.message }));
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] space-y-6 animate-in fade-in duration-300">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-indigo-100 animate-pulse"></div>
          <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
        </div>
        <div className="space-y-2 text-center animate-pulse">
          <h2 className="text-xl font-bold text-gray-800">{t("newQuestion.loading")}</h2>
          <p className="text-sm text-gray-500">{t("newQuestion.loadingDesc")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild className="rounded-xl hover:bg-gray-100">
          <Link to="/questions">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            {isEdit ? t("newQuestion.editQuestion") : t("newQuestion.newQuestion")}
          </h1>
          <p className="text-gray-500">
            {isEdit ? t("newQuestion.editDesc") : t("newQuestion.newDesc")}
          </p>
        </div>
      </div>

      <form onSubmit={handleSave} className="grid gap-6 lg:grid-cols-3">
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-none shadow-sm bg-white">
            <CardHeader>
              <CardTitle>{t("newQuestion.mainContent")}</CardTitle>
              <CardDescription>{t("newQuestion.mainContentDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {competence !== "listening" && (
                <div className="space-y-2">
                  <Label htmlFor="title">{t("newQuestion.questionTitle")}</Label>
                  <Input 
                    id="title" 
                    placeholder={t("newQuestion.titlePlaceholder")} 
                    value={title} 
                    onChange={e => setTitle(e.target.value)}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="instruction">{t("newQuestion.instructionLabel")}</Label>
                <Textarea 
                  id="instruction" 
                  placeholder={t("newQuestion.instructionPlaceholder")} 
                  value={instruction}
                  onChange={e => setInstruction(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="question">{t("newQuestion.specificQuestion")}</Label>
                <Textarea 
                  id="question" 
                  placeholder={t("newQuestion.questionPlaceholder")} 
                  value={question}
                  onChange={e => setQuestion(e.target.value)}
                />
              </div>

              {competence === "reading" && (
                <div className="space-y-2">
                  <Label htmlFor="text">{t("newQuestion.textLabel")}</Label>
                  <Textarea 
                    id="text" 
                    placeholder={t("newQuestion.textPlaceholder")} 
                    className="min-h-[180px]"
                    value={text}
                    onChange={e => setText(e.target.value)}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* MCQ Options (Only if type is MCQ) */}
          {type === "mcq" && (
            <Card className="border-none shadow-sm bg-white">
              <CardHeader>
                <CardTitle>{t("newQuestion.mcqAlternatives")}</CardTitle>
                <CardDescription>{t("newQuestion.mcqDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isListeningSectionA ? (
                  // Image alternatives for Listening Section A
                  <div className="grid gap-6 md:grid-cols-2">
                    {(["A", "B", "C", "D"] as const).map((label) => {
                      const fileOrUrl = optionImages[label];
                      const previewUrl = fileOrUrl instanceof File 
                        ? URL.createObjectURL(fileOrUrl) 
                        : (fileOrUrl || "");

                      return (
                        <div key={label} className="space-y-2 border border-gray-100 rounded-xl p-4 bg-gray-50/50">
                          <Label className="font-semibold text-gray-700 flex items-center justify-between">
                            <span>{t("newQuestion.alternativeImageLabel", { label })}</span>
                            {fileOrUrl && (
                              <button 
                                type="button" 
                                onClick={() => setOptionImages({ ...optionImages, [label]: null })}
                                className="text-xs text-red-500 hover:text-red-700 font-medium"
                              >
                                {t("newQuestion.remove")}
                              </button>
                            )}
                          </Label>
                          
                          {fileOrUrl ? (
                            <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm flex items-center justify-center group">
                              <img src={previewUrl} alt={`Preview ${label}`} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <input 
                                  type="file" 
                                  accept="image/png, image/jpeg, image/jpg, image/webp" 
                                  className="hidden" 
                                  id={`upload-${label}`} 
                                  onChange={e => {
                                    if (e.target.files?.[0]) {
                                      setOptionImages({ ...optionImages, [label]: e.target.files[0] });
                                    }
                                  }}
                                />
                                <label htmlFor={`upload-${label}`} className="cursor-pointer bg-white text-gray-800 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-md hover:bg-gray-100 transition-colors">
                                  {t("newQuestion.replace")}
                                </label>
                              </div>
                            </div>
                          ) : (
                            <div className="border border-dashed border-gray-200 bg-white rounded-xl aspect-[4/3] flex flex-col items-center justify-center gap-2 p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                              <input 
                                type="file" 
                                accept="image/png, image/jpeg, image/jpg, image/webp" 
                                className="hidden" 
                                id={`upload-${label}`} 
                                onChange={e => {
                                  if (e.target.files?.[0]) {
                                    setOptionImages({ ...optionImages, [label]: e.target.files[0] });
                                  }
                                }}
                              />
                              <label htmlFor={`upload-${label}`} className="cursor-pointer text-center flex flex-col items-center gap-1.5 w-full h-full justify-center">
                                <ImageIcon className="w-8 h-8 text-gray-400" />
                                <span className="text-xs font-medium text-gray-600">{t("newQuestion.selectImage")}</span>
                                <span className="text-[10px] text-gray-400">.jpg, .png, .webp</span>
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Textual alternatives
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="altA">{t("newQuestion.alternativeLabel", { label: "A" })}</Label>
                      <Input 
                        id="altA" 
                        placeholder={t("newQuestion.defaultTextA")} 
                        value={alternatives.A} 
                        onChange={e => setAlternatives({...alternatives, A: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="altB">{t("newQuestion.alternativeLabel", { label: "B" })}</Label>
                      <Input 
                        id="altB" 
                        placeholder={t("newQuestion.defaultTextB")} 
                        value={alternatives.B} 
                        onChange={e => setAlternatives({...alternatives, B: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="altC">{t("newQuestion.alternativeLabel", { label: "C" })}</Label>
                      <Input 
                        id="altC" 
                        placeholder={t("newQuestion.defaultTextC")} 
                        value={alternatives.C} 
                        onChange={e => setAlternatives({...alternatives, C: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="altD">{t("newQuestion.alternativeLabel", { label: "D" })}</Label>
                      <Input 
                        id="altD" 
                        placeholder={t("newQuestion.defaultTextD")} 
                        value={alternatives.D} 
                        onChange={e => setAlternatives({...alternatives, D: e.target.value})}
                      />
                    </div>
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2 pt-2">
                  <div className="space-y-2">
                    <Label htmlFor="correctAnswer">{t("newQuestion.correctAnswer")}</Label>
                    <Select value={correctAnswer} onValueChange={setCorrectAnswer}>
                      <SelectTrigger id="correctAnswer" className="bg-gray-50 border-none">
                        <SelectValue placeholder={t("newQuestion.selectPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">{t("newQuestion.alternativeLabel", { label: "A" })}</SelectItem>
                        <SelectItem value="B">{t("newQuestion.alternativeLabel", { label: "B" })}</SelectItem>
                        <SelectItem value="C">{t("newQuestion.alternativeLabel", { label: "C" })}</SelectItem>
                        <SelectItem value="D">{t("newQuestion.alternativeLabel", { label: "D" })}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="explanation">{t("newQuestion.explanationLabel")}</Label>
                  <Textarea 
                    id="explanation" 
                    placeholder={t("newQuestion.explanationPlaceholder")} 
                    value={explanation}
                    onChange={e => setExplanation(e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar Controls */}
        <div className="space-y-6">
          <Card className="border-none shadow-sm bg-white">
            <CardHeader>
              <CardTitle>{t("newQuestion.classification")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="competence">{t("newQuestion.competency")}</Label>
                <Select value={competence} onValueChange={(v: any) => setCompetence(v)}>
                  <SelectTrigger id="competence" className="bg-gray-50 border-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reading">Compréhension Écrite (CE)</SelectItem>
                    <SelectItem value="listening">Compréhension Orale (CO)</SelectItem>
                    <SelectItem value="writing">Expression Écrite (EE)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="section">{t("newQuestion.sectionLabel")}</Label>
                <Select value={section} onValueChange={setSection}>
                  <SelectTrigger id="section" className="bg-gray-50 border-none">
                    <SelectValue placeholder={t("newQuestion.selectSection")} />
                  </SelectTrigger>
                  <SelectContent>
                    {competence === "reading" && (
                      <>
                        <SelectItem value="Documents divers">Documents divers</SelectItem>
                        <SelectItem value="Phrases lacunaires / Textes lacunaires">Phrases lacunaires / Textes lacunaires</SelectItem>
                        <SelectItem value="Lecture rapide de textes et de graphiques">Lecture rapide de textes et de graphiques</SelectItem>
                        <SelectItem value="Documents administratifs et professionnels">Documents administratifs et professionnels</SelectItem>
                        <SelectItem value="Articles de presse">Articles de presse</SelectItem>
                      </>
                    )}
                    {competence === "listening" && (
                      <>
                        <SelectItem value="Dialogues associés à des images">Dialogues associés à des images</SelectItem>
                        <SelectItem value="Annonces publiques">Annonces publiques</SelectItem>
                        <SelectItem value="Messages courts / situations téléphoniques">Messages courts / situations téléphoniques</SelectItem>
                        <SelectItem value="Micro-trottoirs">Micro-trottoirs</SelectItem>
                        <SelectItem value="Chroniques radio">Chroniques radio</SelectItem>
                        <SelectItem value="Interviews">Interviews</SelectItem>
                        <SelectItem value="Reportages">Reportages</SelectItem>
                        <SelectItem value="Documents divers">Documents divers</SelectItem>
                      </>
                    )}
                    {competence === "writing" && (
                      <>
                        <SelectItem value="Fait Divers">Section A (Fait Divers - Redação)</SelectItem>
                        <SelectItem value="Letra de Opinião">Section B (Letra de Opinião)</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {competence === "reading" && (section === "Phrases lacunaires / Textes lacunaires" || section === "Lecture rapide de textes et de graphiques") && (
                <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                  <Label htmlFor="subSection" className="text-primary font-bold">{t("newQuestion.internalSubtype")}</Label>
                  <Select value={subSection} onValueChange={setSubSection}>
                    <SelectTrigger id="subSection" className="bg-primary/5 border-primary/20 text-primary font-semibold">
                      <SelectValue placeholder={t("newQuestion.selectSubsection")} />
                    </SelectTrigger>
                    <SelectContent>
                      {section === "Phrases lacunaires / Textes lacunaires" ? (
                        <>
                          <SelectItem value="phrases_lacunaires">Phrases lacunaires</SelectItem>
                          <SelectItem value="textes_lacunaires">Textes lacunaires</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="lecture_rapide_textes">Lecture rapide de textes</SelectItem>
                          <SelectItem value="lecture_rapide_graphiques">Lecture rapide de graphiques</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Auto-difficulty info card */}
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3.5 space-y-0.5 animate-in fade-in duration-300">
                <div className="flex items-center gap-1.5 text-indigo-700 mb-1">
                  <Zap className="w-3.5 h-3.5" />
                  <span className="text-xs font-semibold uppercase tracking-wider">{t("newQuestion.autoDifficulty")}</span>
                </div>
                <p className="text-sm font-bold text-indigo-900">{autoDifficulty.difficultyLabel}</p>
                <p className="text-xs text-indigo-600">{t("newQuestion.probableLevel", { level: autoDifficulty.probableLevel })}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tags">{t("newQuestion.tagsLabel")}</Label>
                <Input 
                  id="tags" 
                  placeholder={t("newQuestion.tagsPlaceholder")} 
                  value={tags}
                  onChange={e => setTags(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Multimedia Upload Card */}
          <Card className="border-none shadow-sm bg-white">
            <CardHeader>
              <CardTitle>{t("newQuestion.auxiliaryMedia")}</CardTitle>
              <CardDescription>{t("newQuestion.mediaDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Image Upload: visible for all */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-gray-500" /> {t("newQuestion.illustrativeImage")}
                </Label>
                
                {imageUrl || existingImageUrl ? (
                  <div className="border border-gray-100 bg-gray-50/50 rounded-xl p-4 space-y-3 animate-in fade-in duration-300">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700 truncate max-w-[200px]">
                        {imageUrl ? imageUrl.name : t("newQuestion.existingImage")}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setImageUrl(null);
                          setExistingImageUrl(null);
                          setImageRemoved(true);
                        }}
                        className="text-xs text-red-500 hover:text-red-700 font-semibold"
                      >
                        {t("newQuestion.removeImage")}
                      </button>
                    </div>
                    
                    <div className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 bg-white">
                      <img 
                        src={imageUrl ? URL.createObjectURL(imageUrl) : (existingImageUrl || "")} 
                        alt="Preview ilustrativo" 
                        className="w-full h-full object-cover" 
                      />
                    </div>
                    
                    <div className="flex justify-end">
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        id="imageUploadReplace" 
                        onChange={e => {
                          if (e.target.files?.[0]) {
                            setImageUrl(e.target.files[0]);
                            setImageRemoved(false);
                          }
                        }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        asChild
                        className="rounded-lg text-xs font-semibold border-gray-200"
                      >
                        <label htmlFor="imageUploadReplace" className="cursor-pointer">
                          {t("newQuestion.replaceImage")}
                        </label>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="border border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors">
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      id="imageUpload" 
                      onChange={e => {
                        if (e.target.files?.[0]) {
                          setImageUrl(e.target.files[0]);
                          setImageRemoved(false);
                        }
                      }}
                    />
                    <label htmlFor="imageUpload" className="cursor-pointer text-xs text-gray-500 block">
                      {t("newQuestion.clickDragImage")}
                    </label>
                  </div>
                )}
              </div>

              {/* Audio Upload: only visible for Listening */}
              {competence === "listening" && (
                <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                  <Label className="flex items-center gap-2">
                    <Headphones className="w-4 h-4 text-gray-500" /> {t("newQuestion.audioFile")}
                  </Label>
                  
                  {audioUrl || existingAudioUrl ? (
                    <div className="border border-gray-100 bg-gray-50/50 rounded-xl p-4 space-y-3 animate-in fade-in duration-300">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-700 truncate max-w-[200px]">
                          {audioUrl ? audioUrl.name : t("newQuestion.existingAudio")}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setAudioUrl(null);
                            setExistingAudioUrl(null);
                            setAudioRemoved(true);
                          }}
                          className="text-xs text-red-500 hover:text-red-700 font-semibold"
                        >
                          {t("newQuestion.removeAudio")}
                        </button>
                      </div>
                      
                      <AudioPlayerWithSpeed 
                        src={audioUrl ? URL.createObjectURL(audioUrl) : (existingAudioUrl || "")} 
                        className="w-full"
                        compact
                      />
                      
                      <div className="flex justify-end">
                        <input 
                          type="file" 
                          accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac" 
                          className="hidden" 
                          id="audioUploadReplace" 
                          onChange={e => {
                            if (e.target.files?.[0]) {
                              setAudioUrl(e.target.files[0]);
                              setAudioRemoved(false);
                            }
                          }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          asChild
                          className="rounded-lg text-xs font-semibold border-gray-200"
                        >
                          <label htmlFor="audioUploadReplace" className="cursor-pointer">
                            {t("newQuestion.replaceAudio")}
                          </label>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors">
                      <input 
                        type="file" 
                        accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac" 
                        className="hidden" 
                        id="audioUpload" 
                        onChange={e => {
                          if (e.target.files?.[0]) {
                            setAudioUrl(e.target.files[0]);
                            setAudioRemoved(false);
                          }
                        }}
                      />
                      <label htmlFor="audioUpload" className="cursor-pointer text-xs text-gray-500 block">
                        {t("newQuestion.clickDragAudio")}
                      </label>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" type="button" asChild className="flex-1 rounded-xl">
              <Link to="/questions">{t("newQuestion.cancel")}</Link>
            </Button>
            <Button 
              type="submit" 
              disabled={isSaving} 
              className={`flex-1 bg-primary hover:bg-primary/90 text-white rounded-xl shadow-md ${
                isSaving ? "opacity-50 cursor-not-allowed hover:bg-primary/50" : ""
              }`}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? t("newQuestion.saving") : (isEdit ? t("newQuestion.updateQuestion") : t("newQuestion.saveQuestion"))}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
